// 1. The AlgoliaSearchSuggestion component is a React functional component that displays a single search suggestion for a product using Algolia results.
// 2. It shows the product's image, name, and price, with a clickable link to the product's detail page.
// 3. The component extracts information like the product's image and price from the first variant of the product.
// 4. It uses Link from Next.js for navigation and includes a hover effect for better user interaction.
// 5. A click event handler is implemented to prevent default behavior and trigger a custom callback when a suggestion is clicked.

import useLocale from 'helpers/hooks/useLocale';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';
import { Hit as AlgoliaHit } from 'instantsearch.js';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import Price from '../Algolia-search-Page/price';
import { MAIN_CATEGORIES } from 'helpers/constants/categories';
import { sortCategoryLevels } from 'helpers/constants/sortCategoryLevels';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import { CLICKED_OBJECT_ID, SEARCH_QUERY_ID } from 'helpers/constants/algolia';
import { getLocalizedField } from 'helpers/product/productUrl';
import PriceMap from '../AlgoliaPrice/priceMap';
import { useCookies } from 'react-cookie';
type HitHighlightResult = Partial<{
  value: string;
  matchLevel: 'none' | 'partial' | 'full';
  matchedWords: string[];
}>;

type VariantAlgolia = Partial<{
  id?: string;
  key?: string;
  sku?: string;
  attributes?: {
    flex?: string;
    gender?: string;
    hand?: string;
    inStockDate?: string;
    isDiscontinued?: boolean;
    isPerpetualStock?: boolean;
    loft?: string;
    onlineDate?: string;
    optionsOrder?: string[];
    preOrderBackOrderAllocation?: number;
    preOrderBackOrderHandling?: string;
    shafttype?: string;
  };
  searchableAttributes?: Record<string, any>;
  images?: string[];
  prices?: Record<string, any>;
  isInStock?: boolean;
  inventory?: Record<string, any>;
}>;

type BaseHit = Partial<{
  productType?: string;
  name?: {
    'en-GB'?: string;
  };
  key?: string;
  slug?: {
    'en-GB'?: string;
  };
  categories?: {
    'en-GB'?: {
      lvl0?: string[];
      lvl1?: string[];
      lvl2?: string[];
      lvl3?: string[];
    };
  };
  categoryKeys?: {
    'en-GB'?: string[];
  };
  attributes?: {
    brand?: string;
    collectAtStore?: boolean;
    isCustomFit?: boolean;
    isGiftcard?: boolean;
    isPersonalized?: boolean;
    productUrl?: string;
    weight?: number;
  };
  createdAt?: string;
  masterVariantID?: string;
  categoryPageId?: string[];
  variantIDs?: string[];
  _tags?: string[];
  variants?: VariantAlgolia[];
  objectID?: string;
  _highlightResult?: {
    name?: {
      'en-GB'?: HitHighlightResult;
    };
    categories?: {
      'en-GB'?: {
        lvl0?: HitHighlightResult[];
        lvl1?: HitHighlightResult[];
      };
    };
    categoryKeys?: {
      'en-GB'?: HitHighlightResult[];
    };
  };
  __position?: number;
  images?: string;
  variantCode?: string;
  colourDisplay?: string;
}>;
type ExtendedHit = AlgoliaHit<BaseHit>;

type AlgoliaSearchSuggestionProps = {
  hits?: {
    hit: BaseHit;
  };
  onSuggestionClick: () => void;
  matchingProduct: ExtendedHit | undefined | null;
  searchConfiguration: any;
  queryString: string | undefined;
  isSearchPage: boolean;
  queryId?: string;
  position?: number;
};

interface ProductName {
  [locale: string]: string;
}

const AlgoliaSearchSuggestion = ({
  hits,
  onSuggestionClick,
  matchingProduct,
  searchConfiguration,
  queryString,
  isSearchPage,
  queryId,
  position,
}: AlgoliaSearchSuggestionProps) => {
  const [isSearchSuggestion, setIsSearchSuggestion] = useState(true);
  // const variant = hits?.hit?.variants?.[0];
  const variant = hits?.hit;
  const productImage = variant?.images?.[0];
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const productPrice = isGBP
    ? (hits as any)?.hit?.standalonePrice?.[0]?.priceGB
    : (hits as any)?.hit?.standalonePrice?.[0]?.priceEU;
  const mrrpPrice = isGBP
    ? (hits as any)?.hit?.standalonePrice?.[0]?.mrrpPriceGB
    : (hits as any)?.hit?.standalonePrice?.[0]?.mrrpPriceEU;
  const { locale, fallbackLocale, localeKey } = useLocale();
  const convertProductNameToUrl = (input: string) => {
    const urlformat = `${input?.toLowerCase()?.replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url.replace(/,/g, '');
  };
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string | null = localeSelection?.currency || null;
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || productPrice <= 0 || mrrpPrice === productPrice || mrrpPrice < productPrice) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - productPrice) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, productPrice]);
  if (/^[^\w\s]+$/.test(queryString as any)) {
    return <></>;
  }
  const lvl0 =
    (hits?.hit?.categories as any)?.[localeKey]?.lvl0 ?? (hits?.hit?.categories as any)?.[fallbackLocale]?.lvl0 ?? [];
  const lvl = sortCategoryLevels(lvl0, MAIN_CATEGORIES);

  const selectedCategory =
    lvl?.find((cat: string) => !cat?.includes('BRANDS')) ?? lvl?.find((cat: string) => cat?.includes('BRANDS'));

  let finalCategory;
  // as of now we are hard coded the "All Products" category need to optimise later
  if (selectedCategory?.split(' > ')[0] === 'All Products') {
    finalCategory = 'products';
  } else {
    finalCategory = !selectedCategory?.split(' > ')?.[0] ? 'products' : selectedCategory?.split(' > ')?.[0];
  }

  const productName = getLocalizedField(hits?.hit?.name, localeKey, fallbackLocale);
  const name = getLocalizedField(hits?.hit?.name, localeKey, fallbackLocale);
  const slug = getLocalizedField(hits?.hit?.slug, localeKey, fallbackLocale);

  return (
    <>
      {(hits as any)?.hit?.standalonePrice.length !== 0 && !isSearchPage && (
        <div>
          <Link
            href={`/${finalCategory?.toLowerCase()?.replace(' ', '-')}/${convertProductNameToUrl(
              name,
            )}${slug}.html?variantCode=${variant?.variantCode}`}
          >
            <div className="flex cursor-pointer items-center justify-between gap-x-2 border-b px-10 py-15 transition-colors duration-200 hover:bg-gray-100">
              <div className="flex items-center">
                <div className="max-w-[50px] pr-10">
                  <img
                    src={productImage}
                    alt={productName}
                    className="h-full w-full rounded-lg object-cover shadow-sm"
                  />
                </div>
                <p className="w-[140px] text-sm font-medium text-gray-900">{productName}</p>
              </div>
              <div className="flex items-center">
                {productPrice && (
                  <PriceMap
                    isSearchSuggestion={isSearchSuggestion}
                    price={productPrice}
                    discountedPercentage={discountPercentage}
                    mrrpPrice={mrrpPrice}
                  />
                )}
                <span className="text-[#127e57]">
                  {' '}
                  <ChevronRightIcon
                    className="mt-2 h-15 w-18 font-bold text-[#127e57]"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                  />
                </span>
              </div>
            </div>
          </Link>
        </div>
      )}
    </>
  );
};

export default AlgoliaSearchSuggestion;
